import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  DateField,
  Field,
  Page,
  PageHeader,
  Select,
  Table,
  backLinkClasses,
  linkTextClasses,
  type Column,
} from '@evogard/ui';
import { formatInstant, loadFailureMessage } from '@evogard/domain';
import type { DeskMoveRow, DeskMovesPage, DeskMovesQuery } from '@evogard/contract';
import { api } from '../../../lib/api.js';
import { useDebouncedTerm } from '../../../lib/debounce.js';
import { useSessionContext } from '../../../lib/session.js';
import { CardDrawer } from './CardDrawer.js';
import type { DeskTenant } from './deskBoard.js';
import { DeskFocusProvider } from './deskFocus.js';
import { DeskMoveProvider } from './DeskMoveDialog.js';
import { DESK_KEY, useDeskSummaries } from './useDesk.js';

const PAGE = 50;

/** Uma linha, com a base de onde ela veio. */
interface Row extends DeskMoveRow {
  tenant: DeskTenant;
}

/**
 * As Movimentações da Mesa — o log que o dono pediu em 20/10/2026: o que
 * aconteceu com cada card, de quem para quem, quem fez, e quando foi o sistema,
 * qual verificação.
 *
 * Junta as duas fontes que a API devolve já descritas: os eventos da Mesa e as
 * movimentações que vieram do Kanban (selo "Kanban"). A busca procura no nosso
 * número, no nome do cliente, na placa e no nome de quem fez ou recebeu o card.
 *
 * É de quem acompanha (`billing.desk:acompanhar`), como o Aproveitamento. O
 * operador vê o histórico de cada card na lateral dele.
 *
 * Uma chamada por base, e a tela intercala pelo horário: a base que recusa
 * (403) fica de fora, avisada.
 */
export function DeskMovesScreen() {
  return (
    <DeskFocusProvider>
      <DeskMoveProvider>
        <Moves />
      </DeskMoveProvider>
    </DeskFocusProvider>
  );
}

function Moves() {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<DeskMovesQuery['kind']>('movimentos');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [only, setOnly] = useState('');
  const [pages, setPages] = useState(1);
  const [openCard, setOpenCard] = useState<{ tenant: DeskTenant; id: number } | null>(null);
  const q = useDebouncedTerm(search.trim());

  const { data: context } = useSessionContext();
  const { ready } = useDeskSummaries({});
  const tenants: DeskTenant[] = (context?.tenants ?? [])
    .map((t) => ({ id: t.id, slug: t.slug, name: t.nome }))
    .filter((t) => only === '' || t.slug === only);

  const query = {
    q: q.length >= 2 ? q : undefined,
    kind,
    from: from || undefined,
    to: to || undefined,
  };

  const results = useQueries({
    queries: tenants.map((tenant) => ({
      queryKey: [...DESK_KEY, 'movimentacoes', tenant.slug, query, pages],
      queryFn: async (): Promise<{ items: Row[]; next: string | null } | null> => {
        const items: Row[] = [];
        let cursor: string | undefined;
        let next: string | null = null;
        for (let i = 0; i < pages; i++) {
          const r = await api.billingDesk.moves({
            params: { tenant: tenant.slug },
            query: { ...query, cursor, limit: PAGE },
          });
          /*
           * Só o 403 é "seu grupo não pode": a base vem dos vínculos da pessoa,
           * então um 404 aqui é a API sem a rota — e dizer "sem permissão" a um
           * admin mandaria procurar o defeito no lugar errado (20/10/2026).
           */
          if (r.status === 403) return null;
          if (r.status !== 200) {
            throw new Error(loadFailureMessage(r.status, `as movimentações da ${tenant.name}`));
          }
          const body: DeskMovesPage = r.body;
          items.push(...body.items.map((m) => ({ ...m, tenant })));
          next = body.next;
          if (next === null) break;
          cursor = next;
        }
        return { items, next };
      },
    })),
  });

  const refused = tenants.filter((_, i) => results[i]?.data === null);
  const errors = results.flatMap((r) => (r.error ? [r.error.message] : []));
  const streams = results.flatMap((r) => (r.data ? [r.data] : []));
  const { visible, hasMore } = mergeByInstant(streams);
  const loading = results.some((r) => r.isLoading);
  const fetching = results.some((r) => r.isFetching);
  const reset = () => setPages(1);

  return (
    <Page measure="cheia">
      <PageHeader
        title="Movimentações"
        back={
          <Link to="/billing/desk" className={backLinkClasses()}>
            Mesa de cobrança
          </Link>
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-80">
          <Field
            label="Buscar"
            placeholder="Nosso número, cliente, placa ou colaborador"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              reset();
            }}
          />
        </div>
        <div className="w-44">
          <Select
            label="Tipo"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as DeskMovesQuery['kind']);
              reset();
            }}
          >
            <option value="movimentos">Movimentações</option>
            <option value="edicoes">Edições</option>
            <option value="tudo">Tudo</option>
          </Select>
        </div>
        <div className="w-44">
          <DateField
            label="De"
            value={from}
            onChange={(v) => {
              setFrom(v);
              reset();
            }}
          />
        </div>
        <div className="w-44">
          <DateField
            label="Até"
            value={to}
            onChange={(v) => {
              setTo(v);
              reset();
            }}
          />
        </div>
        {(context?.tenants.length ?? 0) > 1 && (
          <div className="w-44">
            <Select
              label="Base"
              value={only}
              onChange={(e) => {
                setOnly(e.target.value);
                reset();
              }}
            >
              <option value="">As duas</option>
              {context!.tenants.map((t) => (
                <option key={t.slug} value={t.slug}>
                  {t.nome}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <p className="text-xs text-texto-suave">
        As linhas com o selo "Kanban" vieram do sistema antigo, com quem fez escrito como ele gravava. A busca
        precisa de pelo menos duas letras.
      </p>

      {errors.map((e) => (
        <Alert key={e} tone="erro">
          {e}
        </Alert>
      ))}
      {refused.length > 0 && (
        <Alert tone="informacao">
          Você não vê as movimentações de {refused.map((t) => t.name).join(' e ')}: lá seu grupo não tem a
          permissão de acompanhar a Mesa.
        </Alert>
      )}

      <Table<Row>
        label="Movimentações dos cards"
        empty="Nenhuma movimentação com esse filtro. Mude a busca, o tipo ou o período."
        items={visible}
        rowKey={(m) => `${m.tenant.slug}-${m.id}`}
        loading={loading}
        paginate={false}
        columns={columns((m) => setOpenCard({ tenant: m.tenant, id: m.card.id }))}
      />
      {hasMore && (
        <div>
          <Button variant="contorno" loading={fetching} onClick={() => setPages((p) => p + 1)}>
            Carregar mais
          </Button>
        </div>
      )}

      {openCard && (
        <CardDrawer
          key={`${openCard.tenant.slug}-${openCard.id}`}
          tenant={openCard.tenant}
          id={openCard.id}
          summary={ready.find((s) => s.tenant.slug === openCard.tenant.slug)?.summary}
          onClose={() => setOpenCard(null)}
        />
      )}
    </Page>
  );
}

function columns(onOpen: (m: Row) => void): Array<Column<Row>> {
  return [
    { title: 'Quando', cell: (m) => <span className="whitespace-nowrap">{formatInstant(m.at)}</span> },
    { title: 'Base', cell: (m) => <Badge>{m.tenant.slug.toUpperCase()}</Badge> },
    {
      title: 'Cliente',
      cell: (m) => (
        <div className="flex min-w-0 flex-col">
          <button type="button" onClick={() => onOpen(m)} className={linkTextClasses('text-left font-medium')}>
            {m.card.name ?? 'Sem nome'}
          </button>
          <span className="text-xs text-texto-suave">
            {[m.card.nossoNumero ? `nosso nº ${m.card.nossoNumero}` : null, m.card.plates.join(', ') || null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </div>
      ),
    },
    {
      title: 'O que aconteceu',
      cell: (m) => (
        <div className="flex flex-col">
          <span className="font-medium text-texto">{m.title}</span>
          {m.details && <span className="text-xs text-texto-suave">{m.details}</span>}
        </div>
      ),
    },
    {
      title: 'De → Para',
      cell: (m) =>
        m.from || m.to ? (
          <span>
            {m.from ?? '—'} → {m.to ?? '—'}
          </span>
        ) : (
          <span className="text-texto-suave">—</span>
        ),
    },
    {
      title: 'Quem',
      cell: (m) => (m.actor ? m.actor : <span className="text-texto-suave">Sistema — {m.via ?? 'rotina'}</span>),
    },
    {
      title: 'Origem',
      cell: (m) => (m.source === 'kanban' ? <Badge tone="alerta">Kanban</Badge> : <Badge>Mesa</Badge>),
    },
  ];
}

/**
 * As bases intercaladas pelo horário, da mais recente para a mais antiga.
 *
 * Só aparece o que não pode mudar de lugar quando a próxima página chegar: se
 * uma base ainda tem página, nada mais antigo que a última linha carregada dela
 * entra — senão uma linha da outra base apareceria antes de uma desta que
 * ainda nem foi pedida.
 */
function mergeByInstant(streams: Array<{ items: Row[]; next: string | null }>): {
  visible: Row[];
  hasMore: boolean;
} {
  const all = streams.flatMap((s) => s.items).sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id));
  const pending = streams.filter((s) => s.next !== null && s.items.length > 0);
  if (pending.length === 0) return { visible: all, hasMore: streams.some((s) => s.next !== null) };
  const boundary = pending.map((s) => s.items[s.items.length - 1]!.at).sort().at(-1)!;
  return { visible: all.filter((m) => m.at >= boundary), hasMore: true };
}
